import { useQuery } from '@tanstack/react-query'
import { Hero } from './components/hero'
import { DeferRender } from '@/shared/ui/defer-render'
import { Seo, organizationSchema, personSchema, websiteSchema } from '@/shared/seo/seo'
import { useTranslation, defineT } from '@/shared/i18n/use-translation'
import { fetchPageSections, resolveSectionOrder } from '@/shared/lib/page-sections'
import { HOME_SECTIONS, HOME_SECTION_MAP, type HomeSectionKey } from './sections'

const T = defineT({
  title: {
    ar: 'عبدالله عيسى | كورسات وأدوات الذكاء الاصطناعي والتصميم',
    en: 'Abdallah Issa | AI Courses & Tools for Design and Development',
  },
  description: {
    ar: 'تعلم الذكاء الاصطناعي للتصميم وصناعة المحتوى والبرمجة. كورسات عملية من الصفر للاحتراف + أدوات مجانية تعمل في المتصفح + مكتبة 300 برومبت. خبرة 10 سنوات، 1500+ طالب.',
    en: 'A decade of design and engineering, focused on AI for images, video, and creative content. Production-grade courses, tools, and prompts.',
  },
})

/**
 * Home — section order + visibility are admin-controlled via
 * `/api/home-sections`. The Hero is always rendered (owns the LCP).
 * Below-the-fold sections come from the registry in `sections.ts`,
 * filtered + ordered by the API config. If the API is unavailable we
 * fall back to the full static order (every section visible).
 *
 * Section layout direction is still locked by §6.7.1 — the admin can
 * only hide / reorder the known sections, not invent new ones.
 */
export default function HomePage() {
  const { t } = useTranslation(T)

  // Website + Person + Organization schemas for the home page.
  const jsonLd = [websiteSchema(), personSchema(), organizationSchema()]

  const { data: config } = useQuery({
    queryKey: ['page-sections', 'home'],
    queryFn: ({ signal }) => fetchPageSections('home', signal),
    staleTime: 5 * 60 * 1000,
  })

  // Resolve the ordered, visible section keys. `config === undefined`
  // means still loading; `null` means the API failed → use the static
  // registry so the home is never blank.
  const orderedKeys = resolveSectionOrder<HomeSectionKey>(
    config,
    HOME_SECTIONS.map((s) => s.key),
    new Set(Object.keys(HOME_SECTION_MAP)),
  )

  return (
    <>
      <Seo
        title={t('title')}
        description={t('description')}
        path="/"
        noSuffix
        jsonLd={jsonLd}
      />
      <Hero />
      {/* Below-the-fold sections are deferred so their data fetches,
          image grids, and motion don't compete with the hero (LCP) for
          network + main-thread time. Each reserves height to avoid CLS. */}
      {orderedKeys.map((key) => {
        const def = HOME_SECTION_MAP[key]
        if (!def) return null
        const { Component } = def
        if (def.minHeight === 0) {
          return <Component key={key} />
        }
        return (
          <DeferRender key={key} minHeight={def.minHeight}>
            <Component />
          </DeferRender>
        )
      })}
    </>
  )
}
